(function (root, factory) {
  var bridge = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = bridge;
    return;
  }

  bridge.install(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var ALLOWED_EVENT_TYPES = Object.freeze([
    'Subscribe',
    'Contact',
    'Lead',
    'Application',
    'Signup',
  ]);
  var CONFIG_ID = 'AceiiAmazonAatConfig';
  var COUNTRY_COOKIE = 'aceii_aat_country';
  var CUSTOM_EVENT_NAME = 'aceiilab:form_success';
  var PENDING_KEY = 'aceii_aat_pending_form';
  var PENDING_TTL_MS = 10 * 60 * 1000;

  function normalizeEmail(value) {
    if (!value) return '';
    var email = String(value).trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
  }

  function normalizePhone(value, dialingPrefix) {
    if (!value) return '';
    var raw = String(value).trim();
    var hasInternationalPrefix = raw.charAt(0) === '+' || raw.indexOf('00') === 0;

    if (!hasInternationalPrefix && !dialingPrefix) return '';
    if (!hasInternationalPrefix) {
      raw = String(dialingPrefix) + raw.replace(/^0+/, '');
    }

    var digits = raw.replace(/\D/g, '');
    if (raw.indexOf('00') === 0) digits = digits.replace(/^00/, '');
    return digits.length >= 8 && digits.length <= 15 ? digits : '';
  }

  function normalizeCountry(value) {
    var country = String(value || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(country) ? country : '';
  }

  function canUseIdentifiers(consent) {
    return Boolean(
      consent &&
        consent.analyticsProcessingAllowed === true &&
        consent.marketingAllowed === true &&
        consent.saleOfDataAllowed === true,
    );
  }

  async function sha256(value) {
    var cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
    if ((!cryptoApi || !cryptoApi.subtle) && typeof require === 'function') {
      cryptoApi = require('node:crypto').webcrypto;
    }
    if (!cryptoApi || !cryptoApi.subtle || typeof TextEncoder === 'undefined') return '';

    var bytes = new TextEncoder().encode(String(value));
    var digest = await cryptoApi.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map(function (byte) {
        return byte.toString(16).padStart(2, '0');
      })
      .join('');
  }

  function classifyFormDescriptor(descriptor) {
    var form = descriptor || {};
    var action = String(form.action || '').toLowerCase();
    var tags = String(form.tags || '').toLowerCase();

    if (form.isNewsletter || tags.split(',').some(function (tag) {
      return tag.trim() === 'newsletter';
    })) {
      return ['Subscribe'];
    }
    if (form.isPartner) return ['Lead', 'Application'];
    if (form.isRegistration) return ['Signup'];
    if (action.indexOf('/contact') !== -1) return ['Contact'];
    return [];
  }

  function hasSuccessfulRedirect(context, pending) {
    var current = context || {};
    var eventTypes = (pending && pending.eventTypes) || [];
    var params = new URLSearchParams(current.search || '');
    var contactSuccess = params.get('contact_posted') === 'true';
    var customerSuccess = params.get('customer_posted') === 'true';
    var contactTypes = ['Contact', 'Lead', 'Application'];

    if (
      contactSuccess &&
      eventTypes.some(function (eventType) {
        return contactTypes.indexOf(eventType) !== -1;
      })
    ) {
      return true;
    }
    if (customerSuccess && eventTypes.indexOf('Subscribe') !== -1) return true;
    if (current.customerLoggedIn && eventTypes.indexOf('Signup') !== -1) return true;
    return false;
  }

  function isPendingFresh(pending, now) {
    var savedAt = Number(pending && pending.savedAt);
    var currentTime = Number(now);
    return (
      Number.isFinite(savedAt) &&
      Number.isFinite(currentTime) &&
      currentTime >= savedAt &&
      currentTime - savedAt <= PENDING_TTL_MS
    );
  }

  async function buildPublishPayload(pending, eventType) {
    if (ALLOWED_EVENT_TYPES.indexOf(eventType) === -1) return null;

    var source = pending || {};
    var payload = {
      eventType: eventType,
      countryCode: normalizeCountry(source.countryCode),
      consent: canUseIdentifiers(source.consent) ? 'granted' : 'denied',
    };

    if (canUseIdentifiers(source.consent) && payload.countryCode) {
      var email = normalizeEmail(source.email);
      var phone = /^[0-9]{8,15}$/.test(source.phone || '')
        ? source.phone
        : normalizePhone(source.phone);
      if (email) payload.hashedEmail = await sha256(email);
      if (phone) payload.hashedPhone = await sha256(phone);
    }

    return Object.keys(payload).reduce(function (result, key) {
      if (payload[key] !== undefined && payload[key] !== null && payload[key] !== '') {
        result[key] = payload[key];
      }
      return result;
    }, {});
  }

  function readConfig(documentObject) {
    var element = documentObject && documentObject.getElementById(CONFIG_ID);
    if (!element) return {};
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function safePrivacyCall(privacyApi, methodName) {
    try {
      return Boolean(
        privacyApi &&
          typeof privacyApi[methodName] === 'function' &&
          privacyApi[methodName](),
      );
    } catch (error) {
      return false;
    }
  }

  function privacySnapshot(root) {
    var privacyApi = root && root.Shopify && root.Shopify.customerPrivacy;
    return {
      analyticsProcessingAllowed: safePrivacyCall(
        privacyApi,
        'analyticsProcessingAllowed',
      ),
      marketingAllowed: safePrivacyCall(privacyApi, 'marketingAllowed'),
      preferencesProcessingAllowed: safePrivacyCall(
        privacyApi,
        'preferencesProcessingAllowed',
      ),
      saleOfDataAllowed: safePrivacyCall(privacyApi, 'saleOfDataAllowed'),
    };
  }

  function formDescriptor(form) {
    var tagsInput = form.querySelector('input[name="contact[tags]"]');
    var passwordInput = form.querySelector('input[name="customer[password]"]');
    return {
      action: form.getAttribute('action') || form.action || '',
      tags: tagsInput ? tagsInput.value : '',
      isNewsletter: form.classList.contains('hdt-newsletter-form'),
      isPartner: Boolean(form.closest('.aceii-partner-apply')),
      isRegistration:
        form.classList.contains('hdt-signup-form') || Boolean(passwordInput),
    };
  }

  function firstFieldValue(form, selectors) {
    for (var index = 0; index < selectors.length; index += 1) {
      var field = form.querySelector(selectors[index]);
      if (field && field.value) return field.value;
    }
    return '';
  }

  function createPendingRecord(form, config, root) {
    var eventTypes = classifyFormDescriptor(formDescriptor(form));
    if (!eventTypes.length) return null;

    var phonePrefix = firstFieldValue(form, [
      'select[name="contact[Phone Code]"]',
      'input[name="contact[Phone Code]"]',
    ]);
    var phone = firstFieldValue(form, [
      'input[type="tel"]',
      'input[name="contact[Phone]"]',
      'input[name="contact[phone]"]',
    ]);

    return {
      eventTypes: eventTypes,
      email: normalizeEmail(
        firstFieldValue(form, [
          'input[type="email"]',
          'input[name="contact[email]"]',
          'input[name="customer[email]"]',
        ]),
      ),
      phone: normalizePhone(phone, phonePrefix),
      countryCode: normalizeCountry(config.countryCode),
      consent: privacySnapshot(root),
      savedAt: Date.now(),
    };
  }

  function savePending(storage, pending) {
    if (!storage || !pending) return;
    try {
      storage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (error) {
      return;
    }
  }

  function loadPending(storage) {
    if (!storage) return null;
    try {
      return JSON.parse(storage.getItem(PENDING_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function clearPending(storage) {
    if (!storage) return;
    try {
      storage.removeItem(PENDING_KEY);
    } catch (error) {
      return;
    }
  }

  function waitForAnalytics(root, attemptsRemaining) {
    var analyticsApi = root && root.Shopify && root.Shopify.analytics;
    if (analyticsApi && typeof analyticsApi.publish === 'function') {
      return Promise.resolve(analyticsApi);
    }
    if (attemptsRemaining <= 0) return Promise.resolve(null);
    return new Promise(function (resolve) {
      root.setTimeout(function () {
        resolve(waitForAnalytics(root, attemptsRemaining - 1));
      }, 100);
    });
  }

  async function processPending(root, config) {
    var storage = root.sessionStorage;
    var pending = loadPending(storage);
    if (!pending) return;

    if (!isPendingFresh(pending, Date.now())) {
      clearPending(storage);
      return;
    }

    var success = hasSuccessfulRedirect(
      {
        search: root.location && root.location.search,
        pageType: config.pageType,
        customerLoggedIn: config.customerLoggedIn === true,
      },
      pending,
    );
    if (!success) return;

    clearPending(storage);
    var analyticsApi = await waitForAnalytics(root, 20);
    if (!analyticsApi) return;

    for (var index = 0; index < pending.eventTypes.length; index += 1) {
      var payload = await buildPublishPayload(pending, pending.eventTypes[index]);
      if (!payload) continue;
      try {
        await analyticsApi.publish(CUSTOM_EVENT_NAME, payload);
      } catch (error) {
        continue;
      }
    }
  }

  function install(root) {
    if (!root || !root.document) return null;
    var config = readConfig(root.document);

    root.document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        savePending(root.sessionStorage, createPendingRecord(form, config, root));
      },
      true,
    );

    processPending(root, config);
    return {config: config};
  }

  return {
    ALLOWED_EVENT_TYPES: ALLOWED_EVENT_TYPES,
    COUNTRY_COOKIE: COUNTRY_COOKIE,
    CUSTOM_EVENT_NAME: CUSTOM_EVENT_NAME,
    PENDING_KEY: PENDING_KEY,
    PENDING_TTL_MS: PENDING_TTL_MS,
    buildPublishPayload: buildPublishPayload,
    canUseIdentifiers: canUseIdentifiers,
    classifyFormDescriptor: classifyFormDescriptor,
    createPendingRecord: createPendingRecord,
    hasSuccessfulRedirect: hasSuccessfulRedirect,
    install: install,
    isPendingFresh: isPendingFresh,
    normalizeCountry: normalizeCountry,
    normalizeEmail: normalizeEmail,
    normalizePhone: normalizePhone,
    privacySnapshot: privacySnapshot,
    processPending: processPending,
    sha256: sha256,
  };
});
